import {
  Configure,
  InstantSearch,
  Index,
  useInstantSearch,
} from "react-instantsearch";

import { getSearchClient } from "@/components/helpers/api/algolia-utils";
import { getCurrentSite } from "@/components/helpers/configuration/utils";
import { buildArticleContentFilter } from "@/components/helpers/search/articleSearchFilter";
import { Box } from "@/components/layout/flexbox";
import ContentContainer from "@/components/pages_flexible/article_series/ContentContainer";
import { ARTICLE_INDEX } from "@/components/pieces/algolia/indexes";
import ArticleSeriesDetailView from "@/components/pieces/learn/ArticleSeriesDetailView";
import ArticleSeriesBlock from "@/components/pieces/learn/search/ArticleSeriesBlock";
import { getUser } from "@/components/session/JwtTokenManager";
import { ArticleSeries } from "@/graphql";

interface ReadStatus {
  completedItemCount?: number;
  inprogressItemCount?: number;
  articleReadStatusById?: Record<string, string>;
}

interface ArticleHit {
  article_id?: string;
  objectID: string;
  url_slug: string;
  hero_image: string;
  time_to_read: number;
  [key: string]: any;
}

interface ArticleHitsProps {
  articleSeries: Partial<ArticleSeries>;
  readStatus: ReadStatus;
}

function ArticleHits({
  articleSeries = {},
  readStatus = {},
}: ArticleHitsProps) {
  const { results } = useInstantSearch();
  const hits = results.hits as ArticleHit[];

  const {
    completedItemCount = 0,
    inprogressItemCount = 0,
    articleReadStatusById = {},
  } = readStatus;

  const articles = hits.map((hit) => ({
    ...hit,
    id: hit.article_id || hit.objectID,
    urlSlug: hit.url_slug,
    heroImage: hit.hero_image,
    timeToRead: hit.time_to_read,
    currentUserReadStatus:
      articleReadStatusById[hit.article_id || hit.objectID],
  }));
  const { title, description, urlSlug } = articleSeries;

  const data = {
    articles,
    totalItemCount: hits.length,
    completedItemCount,
    inprogressItemCount,
    description,
    title,
    urlSlug,
  };

  return <ArticleSeriesDetailView data={data} />;
}

interface ArticleSeriesSearchProps {
  indexName?: string;
  readStatus?: ReadStatus;
  articleSeries?: ArticleSeries;
  [key: string]: any;
}

interface RelatedArticleSeriesCarouselsProps {
  articleSeriesItems?: Array<Pick<ArticleSeries, "id" | "urlSlug" | "title">>;
}

function RelatedArticleSeriesCarousels({
  articleSeriesItems,
}: RelatedArticleSeriesCarouselsProps) {
  if (!articleSeriesItems?.length) {
    return null;
  }

  return (
    <ul style={{ listStyle: "none", padding: 0 }}>
      {articleSeriesItems.map((series) => (
        <li key={series.id}>
          <ArticleSeriesBlock
            urlSlug={series.urlSlug}
            articleSeriesId={series.id}
            title={series.title}
          />
        </li>
      ))}
    </ul>
  );
}

export default function ArticleSeriesSearch({
  indexName = ARTICLE_INDEX,
  readStatus = {},
  articleSeries = {} as ArticleSeries,
  ...props
}: ArticleSeriesSearchProps) {
  const { urlSlug = "" } = articleSeries;

  const currentSite = getCurrentSite();
  const user = getUser();

  const articlesFilters = buildArticleContentFilter({
    visibleOnSite: currentSite,
    showPublicOnly: !user,
  });

  const articleSeriesArticleFilters = buildArticleContentFilter({
    visibleOnSite: currentSite,
    articleSeriesUrlSlug: urlSlug,
    showPublicOnly: !user,
  });

  const configProps =
    indexName === ARTICLE_INDEX
      ? {
          tagFilters: `series_${urlSlug === "other-articles" ? "other" : urlSlug}`,
          filters: articlesFilters,
        }
      : {
          filters: articleSeriesArticleFilters,
        };

  return (
    <Box width={1}>
      <InstantSearch
        searchClient={getSearchClient()}
        indexName={indexName}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        {...props}
      >
        <Configure hitsPerPage={0} />
        <Index indexName={indexName} indexId="articleSeriesArticles">
          <Configure hitsPerPage={200} {...configProps} />
          <ArticleHits
            articleSeries={articleSeries}
            readStatus={readStatus}
            {...props}
          />
        </Index>

        {articleSeries?.relatedSeries?.length > 0 && (
          <ContentContainer>
            <RelatedArticleSeriesCarousels
              articleSeriesItems={articleSeries.relatedSeries}
            />
          </ContentContainer>
        )}
      </InstantSearch>
    </Box>
  );
}
